import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '../api'

// The address of the meter on the page and every meter and inverter there, from
// /meters/{serial}/address (eleena-home services/meter_address.py):
//   kind      regular | cvz (the numbered fake addresses of a collective installation, together) |
//             placeholder (a parking address for wrongly registered meters: only this meter)
//   meters    one per serial: current first with this meter on top, then the replaced ones;
//             each with sources, alive, house_number (its own fake address on a CVZ) and history
//             (the device periods, e.g. Cast4all then SolarEdge for a switched meter)
//   warnings  e.g. history unavailable, parking address
// When the call fails the address holds just this meter, from its detail.
export function useAddressDevices(serialNumber, meterDetail) {
  const { data, isLoading, isError } = useQuery({
    queryKey: computed(() => ['meterAddress', serialNumber.value]),
    queryFn: async () => {
      const result = await fetchData(`${READINGS_API_BASE}/meters/${serialNumber.value}/address`)
      return result?.data ?? null
    },
    enabled: computed(() => !!serialNumber.value),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const kind = computed(() => data.value?.kind ?? 'regular')

  const devices = computed(() => {
    const meters = data.value?.meters
    if (meters?.length) {
      return meters.map((m) => ({ ...m, isThis: m.serial_number === serialNumber.value }))
    }
    if (!meterDetail.value) return []
    return [{
      serial_number: serialNumber.value,
      house_number: meterDetail.value.house_number,
      current: true,
      sources: meterDetail.value.sources ?? [],
      alive: meterDetail.value.alive ?? null,
      valid_from: null,
      valid_to: null,
      first_data_at: null,
      last_data_at: null,
      // still loading or failed: first and last data are not known, which is not the same as none
      dataSpanKnown: false,
      history: [],
      isThis: true,
    }]
  })

  const warnings = computed(() => [
    ...(data.value?.warnings ?? []),
    ...(isError.value ? ['De andere meters op dit adres konden niet worden opgehaald'] : []),
  ])

  return { devices, kind, warnings, isLoading }
}
